<template>
    <div class="checkout-hotel-form">
        <FormV2
            :id="formId"
            :initialValues="initialValues"
            :validationSchema="validationSchema"
            :onValidChange="handleValidChange"
            :onChange="handleFormChange">
            <div v-if="product.Rooms.length" class="checkout-hotel-form-rooms">
                <Notification
                    variant="neutral"
                    icon="InfoCircle"
                    padding="l"
                    class="checkout-hotel-room-disclaimer"
                    :border="false"
                    :tier="1">
                    {{ t({ key: 'checkout.only_a_name_is_necessary' }) }}
                </Notification>
                <Room
                    v-for="(room, index) in product.Rooms"
                    :key="index"
                    :room="room"
                    :index="index"
                    :product="product"
                    :multiple="product.Rooms.length > 1" />
            </div>
        </FormV2>

        <AppliedDiscountField :productId="product.Id" />

        <Copy
            :productId="product.Id"
            :formId="formId"
            :formValid="isValid"
            :formValues="formValues" />
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType, computed, ref } from 'vue';
    import { t } from '@shared/helpers/translation';
    import { CheckoutStepState } from '@/store/checkout/types';
    import { FormContext } from 'vee-validate';
    import { ValidationSchema } from '@ui/components/FormV2/Validations/Validations.interfaces';
    import { HotelPax, HotelProduct, MandatoryFieldId } from '@/api/Cart/cart.interfaces';
    import { isMandatory } from '@shared/helpers/product';
    import {
        isDateBetween,
        isName,
        isRequired,
        isSelected,
        isValidCharacters,
    } from '@ui/components/FormV2/Validations/functions';
    import { FormValues } from '@ui/components/FormV2/Form.interfaces';
    import { useStore } from '@/store';
    import AppliedDiscountField from '@/components/checkoutPageV3/Shared/AppliedDiscount/Field/AppliedDiscountField.vue';
    import FormV2 from '@ui/components/FormV2/Form.vue';
    import Room from './Room/Room.vue';
    import Copy from '../../../../Shared/Copy/Copy.vue';
    import Notification from '@ui/components/Notification/Notification.vue';

    export default defineComponent({
        name: 'HotelForm',
        methods: { t },
        components: { FormV2, Room, Copy, AppliedDiscountField, Notification },
        props: {
            sectionId: { type: String, required: true },
            product: {
                type: Object as PropType<HotelProduct>,
                required: true,
            },
        },
        setup(props) {
            const formId = `form_hotel_${props.product.Id}`;
            const formValues = ref<FormValues | null>(null);

            const store = useStore();

            const fields = computed(() => getFields());

            const states = computed<CheckoutStepState[]>(
                () => store.getters['checkout/getStates']
            );
            const isValid = computed<boolean>(() => {
                const step = states.value.find((x) => x.id === props.sectionId);
                return step ? step.valid : false;
            });

            function getFields() {
                return props.product?.Rooms.map((room, index) =>
                    getMandatoryFields(room.Pax, index)
                );
            }

            function getMandatoryFields(pax: HotelPax, roomIndex: number) {
                const obj = `room${roomIndex}`;

                const fields = [
                    {
                        obj,
                        prop: 'gender',
                        value: pax.Gender,
                        validation: [isSelected],
                    },
                    {
                        obj,
                        prop: 'firstName',
                        value: pax.FirstName,
                        validation: [isRequired, isName],
                    },
                    {
                        obj,
                        prop: 'lastName',
                        value: pax.LastName,
                        validation: [isRequired, isName],
                    },
                    {
                        obj,
                        prop: 'specialRequest',
                        value: pax.SpecialRequest,
                        validation: [isValidCharacters],
                    },
                ];

                if (dobIsMandatory(pax)) {
                    fields.push({
                        obj,
                        prop: 'dateOfBirth',
                        value: pax.DateOfBirth,
                        validation: [
                            isRequired,
                            isDateBetween(new Date('1900-01-01'), new Date()),
                        ],
                    });
                }

                return fields;
            }

            const dobIsMandatory = (pax: HotelPax) => {
                return isMandatory(pax.MandatoryFields, MandatoryFieldId.DATE_OF_BIRTH);
            };

            function handleFormChange({ values }: FormContext) {
                formValues.value = values;
            }

            function handleValidChange(valid: boolean) {
                store.dispatch('checkout/setValid', {
                    id: props.sectionId,
                    valid,
                });
            }

            const validationSchema = computed((): ValidationSchema => {
                const schema: ValidationSchema = {};

                fields.value?.flat().forEach((f) => {
                    const name = `${f.obj}.${f.prop}`;
                    schema[name] = f.validation;
                });

                return schema;
            });

            const initialValues = computed(() => {
                const values: FormValues = {};

                fields.value?.forEach((field) => {
                    values[field[0].obj] = {};

                    field.forEach((f) => {
                        values[field[0].obj][f.prop] = f.value;
                    });
                });

                return values;
            });

            return {
                formId,
                formValues,
                validationSchema,
                initialValues,
                handleFormChange,
                handleValidChange,
                isValid,
            };
        },
    });
</script>

<style lang="scss">
    @forward 'Form.scss';
</style>
